import { Controller } from "stimulus"

interface ErrorResponse {
  success: boolean;
  errors?: Record<string, string[]>;
  redirect_url?: string;
}

export default class extends Controller {
  static targets = ["form", "submitButton", "errorContainer", "resumeInput", "coverLetterInput", "captchaWidget"]

  declare readonly formTarget: HTMLFormElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly errorContainerTarget: HTMLElement
  declare readonly resumeInputTarget: HTMLInputElement
  declare readonly coverLetterInputTarget: HTMLInputElement
  declare readonly captchaWidgetTarget: HTMLElement
  declare readonly hasErrorContainerTarget: boolean
  declare readonly hasResumeInputTarget: boolean
  declare readonly hasCoverLetterInputTarget: boolean
  declare readonly hasCaptchaWidgetTarget: boolean

  private resumeFile: File | null = null
  private coverLetterFile: File | null = null

  connect(): void {
    // Store file references in memory
    this.resumeFile = null
    this.coverLetterFile = null
  }

  // Store resume file when selected
  storeResume(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this.resumeFile = input.files[0]
    }
  }

  // Store cover letter file when selected
  storeCoverLetter(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this.coverLetterFile = input.files[0]
    }
  }

  // Intercept form submission
  async submit(event: Event): Promise<void> {
    event.preventDefault()

    // Disable submit button to prevent double submission
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.submittingText || 'Submitting...'

    const formData = new FormData(this.formTarget)

    // Ensure files are attached from memory
    // This preserves files even after validation failures
    if (this.resumeFile && this.hasResumeInputTarget) {
      formData.set('application[resume]', this.resumeFile)
    }
    if (this.coverLetterFile && this.hasCoverLetterInputTarget) {
      formData.set('application[cover_letter]', this.coverLetterFile)
    }

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      })

      const result: ErrorResponse = await response.json()

      if (result.success) {
        // Redirect to success page
        window.location.href = result.redirect_url!
      } else {
        // Display validation errors
        this.displayErrors(result.errors || {})
        this.resetSubmitButton()
      }
    } catch (error) {
      console.error('Form submission error:', error)
      this.displayErrors({ base: ['An unexpected error occurred. Please try again.'] })
      this.resetSubmitButton()
    }
  }

  displayErrors(errors: Record<string, string[]>): void {
    // Clear previous errors
    this.clearErrors()

    // Display inline errors for each field
    for (const [field, messages] of Object.entries(errors)) {
      if (field === 'base') {
        // Handle base errors separately (show at bottom)
        this.displayBaseErrors(messages)
      } else {
        // Display inline error for this field
        this.displayFieldError(field, messages)
      }
    }

    // Scroll to first error
    const firstError = this.formTarget.querySelector('.field_with_errors')
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  displayFieldError(field: string, messages: string[]): void {
    // Convert field name to proper selector
    // e.g., "custom_questions.0.value" -> "[name='application[custom_questions_attributes][0][value]']"
    const selector = this.buildFieldSelector(field)
    const input = this.formTarget.querySelector(selector)

    // If input not found, try multi-select handling (fields with [] suffix)
    if (!input && selector.endsWith('"]')) {
      this.displayMultiSelectFieldError(selector, messages)
      return
    }

    if (!input) return

    const wrapper = input.closest('.application-form__field, .form-group')
    if (!wrapper) return

    // Mark wrapper with error class
    wrapper.classList.add('field_with_errors')

    // Create error message span (matching SimpleForm's format)
    const errorSpan = document.createElement('span')
    errorSpan.className = 'application-form__error'
    errorSpan.textContent = messages.join(', ')

    // Insert error message after the input element
    // For file inputs, insert after the label
    const insertAfter = wrapper.querySelector('.label-upload') || input
    insertAfter.parentNode!.insertBefore(errorSpan, insertAfter.nextSibling)
  }

  displayMultiSelectFieldError(selector: string, messages: string[]): void {
    // For multi-select fields, look for the hidden input that Rails adds
    // and insert the error message directly after it
    const selectorWithArraySuffix = selector.slice(0, -2) + '[]"]'
    const hiddenInput = this.formTarget.querySelector(`input[type="hidden"]${selectorWithArraySuffix}`)

    if (!hiddenInput) return

    // Create error message span (matching SimpleForm's format)
    const errorSpan = document.createElement('span')
    errorSpan.className = 'application-form__error'
    errorSpan.textContent = messages.join(', ')

    // Insert error directly after the hidden input
    hiddenInput.parentNode!.insertBefore(errorSpan, hiddenInput.nextSibling)
  }

  buildFieldSelector(field: string): string {
    // Handle nested fields (e.g., custom_questions.0.value)
    if (field.includes('.')) {
      const parts = field.split('.')
      let selector = 'application'

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]
        // Check if this part is a number (array index)
        if (/^\d+$/.test(part)) {
          selector += `[${part}]`
        } else {
          // Handle nested attributes convention (e.g., custom_questions -> custom_questions_attributes)
          const fieldName = (i === 0 && part === 'custom_questions')
            ? 'custom_questions_attributes'
            : part
          selector += `[${fieldName}]`
        }
      }

      return `[name="${selector}"]`
    }

    // Simple field (e.g., first_name, email)
    return `[name="application[${field}]"]`
  }

  displayBaseErrors(messages: string[]): void {
    // Create error container if it doesn't exist
    if (!this.hasErrorContainerTarget) {
      const container = document.createElement('div')
      container.setAttribute('data-target', 'application-form.errorContainer')
      this.formTarget.insertBefore(container, this.submitButtonTarget.parentElement)
    }

    // Build error HTML for base errors, reusing the themed error style (#fc1d51)
    // so they stand out the same way inline field errors do.
    let errorHtml = ''
    messages.forEach(error => {
      errorHtml += `<p class="application-form__error">${error}</p>`
    })

    this.errorContainerTarget.innerHTML = errorHtml
  }

  clearErrors(): void {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.innerHTML = ''
    }

    // Remove inline error messages
    this.formTarget.querySelectorAll('.application-form__error').forEach(el => {
      el.remove()
    })

    // Remove field error states
    this.formTarget.querySelectorAll('.field_with_errors').forEach(el => {
      el.classList.remove('field_with_errors')
    })
  }

  resetSubmitButton(): void {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.originalText || 'Submit Application'
    this.resetCaptcha()
  }

  resetCaptcha(): void {
    if (!this.hasCaptchaWidgetTarget) return

    const widgetElement = this.captchaWidgetTarget as any
    if (widgetElement.friendlyChallengeWidget && typeof widgetElement.friendlyChallengeWidget.reset === 'function') {
      widgetElement.friendlyChallengeWidget.reset()
    }
  }
}
